'use client';
/**
 * As the components uses headlessui/react it has to be use as a client side component
 * Unless you will be getting the following error:
 * * Reference: https://stackoverflow.com/questions/78493408/next-js-14-2-error-functions-are-not-valid-as-a-child-of-client-components
 */
import { Fragment, useMemo } from 'react';

import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { UserIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import ChevronDownIcon from 'icons/ChevronDown';
import ImgixImage from 'ui/ImgixImage';
import { checkAdminAccess } from 'utils/checkAdminAccess';
import { TenantDetailsResponse, UserResponse } from 'utils/GeneratedApi';
import { redirect } from 'next/navigation';

type UserMenuProps = {
  showTenantPreferredColor?: boolean;
  user: UserResponse;
  tenant: TenantDetailsResponse;
};

function UserMenu({ showTenantPreferredColor = false, user, tenant }: UserMenuProps) {
  const tHeader = useTranslations('id.header');

  // Show admin button only if user has atleast one admin level permission
  const showAdminButton = useMemo(() => checkAdminAccess(user), [user]);

  const handleLogout = async () => {
    const signOutApiUrl = new URL('/api/auth/signout-cleanup', window.location.origin);

    // Remove the access token from Redis
    await fetch(signOutApiUrl, { method: 'POST' });
    // End the OIDC session
    // window.location.href = '/api/oidc/session/end';
    redirect('/api/oidc/session/end');
  };

  return (
    <div className="flex justify-end items-center">
      <Menu as="div" className="relative inline-block text-left">
        <MenuButton
          id="menu-button"
          className={clsx(
            'flex',
            'items-center',
            'rounded-full',
            'px-2',
            'focus:outline-none',
            'focus:ring-1',
            'focus:ring-offset-1',
            {
              'text-[var(--themeHeaderTextColor)] focus:ring-[var(--themeHeaderTextColor)] focus:ring-offset-[var(--themeHeaderTextColor)]':
                showTenantPreferredColor,
              'text-gray-900 focus:ring-blackSlate focus:ring-offset-gray-100 hover:bg-gray-100 hover:text-gray-900':
                !showTenantPreferredColor,
            },
          )}
        >
          <div className="px-0 sm:px-4 py-1 flex items-center">
            {user?.avatar ? (
              // @TODO: Remove hard coded tenant specific cdn check,  we will move all the images from united cdn to ours
              user?.avatar?.includes('cdn.united.no') ? (
                <Image
                  src={user?.avatar}
                  alt={'profile-picture'}
                  width={40}
                  height={40}
                  className="rounded-full h-8 w-8"
                />
              ) : (
                <ImgixImage
                  src={user?.avatar}
                  alt="profile-picture"
                  options={{
                    h: 40,
                    w: 40,
                    fit: 'crop',
                    crop: 'faces,edges',
                  }}
                  width={40}
                  height={40}
                  classStack="object-contain rounded-full h-8 w-8"
                  loadingType="eager"
                />
              )
            ) : (
              <span className="h-5 w-5">
                <UserIcon />
              </span>
            )}

            <span className={'mx-2 font-medium sm:block hidden'}>
              {user?.nickname || user?.fullName}
            </span>
            <span className="sm:block hidden">
              <ChevronDownIcon />
            </span>
          </div>
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              {showAdminButton ? (
                <MenuItem>
                  {({ focus }) => (
                    <Link
                      href="/admin"
                      className={clsx(
                        {
                          'bg-gray-100 text-gray-900': focus,
                          'text-gray-700': !focus,
                        },
                        'block w-full px-4 py-2 text-left text-sm',
                      )}
                    >
                      {tHeader('button.admin')}
                    </Link>
                  )}
                </MenuItem>
              ) : null}
              {tenant?.name && tenant?.siteUrl ? (
                <MenuItem>
                  {({ focus }) => (
                    <a
                      href={tenant.siteUrl}
                      className={clsx(
                        {
                          'bg-gray-100 text-gray-900': focus,
                          'text-gray-700': !focus,
                        },
                        'block w-full px-4 py-2 text-left text-sm',
                      )}
                    >
                      {tenant.name}
                    </a>
                  )}
                </MenuItem>
              ) : null}
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={clsx(
                      {
                        'bg-gray-100 text-gray-900': focus,
                        'text-gray-700': !focus,
                      },
                      'block w-full px-4 py-2 text-left text-sm cursor-pointer underline',
                    )}
                  >
                    {tHeader('button.signOut')}
                  </button>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  );
}

export default UserMenu;
